import React, { memo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const useStyles = makeStyles({
  chartContainer: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  chartHeader: {
    textAlign: "center",
    marginBottom: "4px",
  },
  chartTitle: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#555",
    marginBottom: "2px",
  },
  chartSubtitle: {
    fontSize: "12px",
    color: "#888",
  },
  chartContent: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    minHeight: 0,
  },
  chartWrapper: {
    flex: "0 0 60%",
    height: "100%",
    position: "relative",
  },
  legendContainer: {
    flex: "0 0 40%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "5px",
    paddingLeft: "8px",
    fontSize: "11px",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  legendColor: {
    width: "10px",
    height: "10px",
    borderRadius: "2px",
    flexShrink: 0,
  },
  legendLabel: {
    color: "#555",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  // Tooltip styles
  tooltipContainer: {
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    border: "1px solid #ddd",
    borderRadius: "4px",
    padding: "8px 12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    fontSize: "11px",
  },
  tooltipTitle: {
    fontWeight: 600,
    color: "#333",
    marginBottom: "4px",
  },
  tooltipRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    color: "#666",
  },
});

// Color palette for categories/brands
const COLORS = [
  "#3f51b5", // Primary blue
  "#f44336", // Red
  "#4caf50", // Green
  "#ff9800", // Orange
  "#9c27b0", // Purple
  "#00bcd4", // Cyan
  "#795548", // Brown
  "#607d8b", // Blue grey
  "#e91e63", // Pink
  "#8bc34a", // Light green
];

// Custom tooltip component — shows both element and company values
function CustomTooltip({
  active,
  payload,
  elementLabel,
  companyLabel,
  elementData,
  companyData,
}) {
  const classes = useStyles();

  if (!active || !payload || !payload.length) return null;

  const name = payload[0]?.payload?.name;
  if (!name) return null;

  const elemItem = (elementData || []).find((d) => d.name === name);
  const compItem = (companyData || []).find((d) => d.name === name);

  return (
    <div className={classes.tooltipContainer}>
      <div className={classes.tooltipTitle}>{name}</div>
      {elemItem && (
        <div className={classes.tooltipRow}>
          <span>{elementLabel || "Elemento"}:</span>
          <span>{elemItem.value?.toFixed(1)}%</span>
        </div>
      )}
      {compItem && (
        <div className={classes.tooltipRow}>
          <span>{companyLabel || "Cia"}:</span>
          <span>{compItem.value?.toFixed(1)}%</span>
        </div>
      )}
    </div>
  );
}

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  elementLabel: PropTypes.string,
  companyLabel: PropTypes.string,
  elementData: PropTypes.array,
  companyData: PropTypes.array,
};

// Process both datasets together to ensure consistent colors and include all unique items
// Rules: max 6 items OR cumulative 90%, whichever comes first (based on combined relevance)
const processDataForDonutPair = (clientData, companyData, colors) => {
  if (
    (!clientData || clientData.length === 0) &&
    (!companyData || companyData.length === 0)
  ) {
    return { client: [], company: [], legend: [] };
  }

  // Get all unique names from both datasets
  const allNames = new Set();
  (clientData || []).forEach((item) => allNames.add(item.name));
  (companyData || []).forEach((item) => allNames.add(item.name));

  // Create a map of name -> combined relevance (max value from either dataset)
  const relevanceMap = new Map();
  allNames.forEach((name) => {
    const clientItem = (clientData || []).find((i) => i.name === name);
    const companyItem = (companyData || []).find((i) => i.name === name);
    const maxValue = Math.max(clientItem?.value || 0, companyItem?.value || 0);
    relevanceMap.set(name, maxValue);
  });

  // Sort names by combined relevance
  const sortedNames = Array.from(allNames).sort(
    (a, b) => relevanceMap.get(b) - relevanceMap.get(a),
  );

  // Determine which names to keep vs group into "Outros"
  const MAX_ITEMS = 7;
  const MAX_CUMULATIVE_PCT = 90;
  const clientTotal = (clientData || []).reduce((sum, i) => sum + i.value, 0);
  const companyTotal = (companyData || []).reduce((sum, i) => sum + i.value, 0);

  const keptNames = new Set();
  let clientCumulative = 0;
  let companyCumulative = 0;

  for (let i = 0; i < sortedNames.length; i++) {
    const name = sortedNames[i];
    if (name === "Outros") continue; // Skip if already named "Outros"

    const clientItem = (clientData || []).find((it) => it.name === name);
    const companyItem = (companyData || []).find((it) => it.name === name);

    const clientPct = clientItem ? (clientItem.value / clientTotal) * 100 : 0;
    const companyPct = companyItem
      ? (companyItem.value / companyTotal) * 100
      : 0;

    // Stop if we've exceeded limits
    const shouldStop =
      i >= MAX_ITEMS ||
      (clientCumulative >= MAX_CUMULATIVE_PCT &&
        companyCumulative >= MAX_CUMULATIVE_PCT);

    if (shouldStop) break;

    keptNames.add(name);
    clientCumulative += clientPct;
    companyCumulative += companyPct;
  }

  // Assign colors to kept names
  const colorMap = new Map();
  let colorIndex = 0;
  Array.from(keptNames).forEach((name) => {
    colorMap.set(name, colors[colorIndex % colors.length]);
    colorIndex++;
  });
  colorMap.set("Outros", "#9e9e9e");

  // Process client data (sorted by value descending)
  const processClientData = (data) => {
    if (!data || data.length === 0) return [];

    const result = [];
    let othersValue = 0;

    data.forEach((item) => {
      if (keptNames.has(item.name)) {
        result.push({
          ...item,
          color: colorMap.get(item.name),
        });
      } else {
        othersValue += item.value;
      }
    });

    // Sort by value descending
    result.sort((a, b) => b.value - a.value);

    if (othersValue > 0) {
      result.push({
        name: "Outros",
        value: othersValue,
        color: "#9e9e9e",
      });
    }

    return result;
  };

  const clientProcessed = processClientData(clientData);

  // Process company data in the same order as client data
  const processCompanyData = (data, clientOrder) => {
    if (!data || data.length === 0) return [];

    // Create a map of company data by name
    const companyMap = new Map();
    let othersValue = 0;

    data.forEach((item) => {
      if (keptNames.has(item.name)) {
        companyMap.set(item.name, {
          ...item,
          color: colorMap.get(item.name),
        });
      } else {
        othersValue += item.value;
      }
    });

    // Build result following client order
    const result = [];
    const addedNames = new Set();

    // First, add items in client order
    clientOrder.forEach((clientItem) => {
      if (clientItem.name !== "Outros" && companyMap.has(clientItem.name)) {
        result.push(companyMap.get(clientItem.name));
        addedNames.add(clientItem.name);
      }
    });

    // Then, add company-only items (sorted by value)
    const companyOnlyItems = [];
    companyMap.forEach((item, name) => {
      if (!addedNames.has(name)) {
        companyOnlyItems.push(item);
      }
    });
    companyOnlyItems.sort((a, b) => b.value - a.value);
    result.push(...companyOnlyItems);

    // Add "Outros" at the end
    if (othersValue > 0) {
      result.push({
        name: "Outros",
        value: othersValue,
        color: "#9e9e9e",
      });
    }

    return result;
  };

  const companyProcessed = processCompanyData(companyData, clientProcessed);

  // Create legend with all kept names + Outros
  const legend = [];
  Array.from(keptNames).forEach((name) => {
    legend.push({ name, color: colorMap.get(name) });
  });
  // Add "Outros" if either dataset has it
  if (
    clientProcessed.some((i) => i.name === "Outros") ||
    companyProcessed.some((i) => i.name === "Outros")
  ) {
    legend.push({ name: "Outros", color: "#9e9e9e" });
  }

  return { client: clientProcessed, company: companyProcessed, legend };
};

/**
 * DonutChart component with two layers
 * Inner ring: Client data (main focus)
 * Outer ring: Company data (faded, thinner, for comparison)
 */
function DonutChart({
  title,
  subtitle,
  clientData,
  companyData,
  innerRingLabel,
  loading,
}) {
  const classes = useStyles();

  // Process both datasets together for consistent colors and complete legend
  const {
    client: clientDataProcessed,
    company: companyDataProcessed,
    legend: legendItems,
  } = React.useMemo(
    () => processDataForDonutPair(clientData, companyData, COLORS),
    [clientData, companyData],
  );

  return (
    <div className={classes.chartContainer}>
      {(title || subtitle) && (
        <div className={classes.chartHeader}>
          {title && <div className={classes.chartTitle}>{title}</div>}
          {subtitle && <div className={classes.chartSubtitle}>{subtitle}</div>}
        </div>
      )}

      <div className={classes.chartContent}>
        {loading ? (
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "120px",
              opacity: 0.5,
            }}
          >
            <div
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                border: "8px solid #e0e0e0",
                borderTopColor: "#999",
                animation: "spin 1s linear infinite",
              }}
            />
            <style>
              {`@keyframes spin { to { transform: rotate(360deg); } }`}
            </style>
          </div>
        ) : (
          <>
            <div className={classes.chartWrapper}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  {/* Outer ring - Company data (faded, thinner) */}
                  <Pie
                    data={companyDataProcessed}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius="85%"
                    outerRadius="95%"
                    paddingAngle={1}
                    strokeWidth={0}
                    animationDuration={300}
                    startAngle={90}
                    endAngle={-270}
                  >
                    {companyDataProcessed.map((entry, index) => (
                      <Cell
                        key={`company-${index}`}
                        fill={entry.color}
                        fillOpacity={0.3}
                      />
                    ))}
                  </Pie>

                  {/* Inner ring - Client/Element data (main focus) */}
                  <Pie
                    data={clientDataProcessed}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius="60%"
                    outerRadius="83%"
                    paddingAngle={1}
                    strokeWidth={0}
                    animationDuration={300}
                    startAngle={90}
                    endAngle={-270}
                  >
                    {clientDataProcessed.map((entry, index) => (
                      <Cell key={`client-${index}`} fill={entry.color} />
                    ))}
                  </Pie>

                  <Tooltip
                    content={
                      <CustomTooltip
                        elementLabel={innerRingLabel || "Elemento"}
                        companyLabel="Cia"
                        elementData={clientDataProcessed}
                        companyData={companyDataProcessed}
                      />
                    }
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Legend */}
            <div className={classes.legendContainer}>
              {legendItems.map((item, index) => (
                <div key={index} className={classes.legendItem}>
                  <div
                    className={classes.legendColor}
                    style={{ backgroundColor: item.color }}
                  />
                  <span className={classes.legendLabel} title={item.name}>
                    {item.name}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

DonutChart.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  clientData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string,
    }),
  ).isRequired,
  companyData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string,
    }),
  ).isRequired,
  innerRingLabel: PropTypes.string,
  loading: PropTypes.bool,
};

export default memo(DonutChart);
